import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useSignalR } from '../signalR/SignalRProvider'
import { fetchTicketById }  from '../../data/signalRVerification/getTicketById'
import { fetchTicketNoteById }  from '../../data/signalRVerification/getTicketNoteById'
import { fetchTicketAttachmentById }  from '../../data/signalRVerification/getTicketAttachmentById'
import { useNotifications } from './notifications/NotificationsContext'
import useLiveUpdateStore from './liveUpdates/liveUpdateStore'

/**
 * Always-mounted component that listens to SignalR ticket messages and keeps
 * the react-query tickets cache up to date, even when TicketsTable is unmounted.
 * Must live inside both <SignalRProvider> and <QueryClientProvider>.
 */

// Delay before invalidating queries for a ticket, to coalesce rapid successive SignalR messages
const DEBOUNCE_MS = 300

const SignalRDataSync = () => {
    const queryClient = useQueryClient()
    const { message } = useSignalR() || {}
    const { addNotification } = useNotifications()
    // One debounce timer per ticketId, so rapid successive messages for the same
    // ticket coalesce into a single invalidation pass regardless of which query keys they touch.
    const debounceTimers = useRef({})
    // Pending query keys per ticketId, de-duplicated by their serialized form,
    // so repeated/overlapping keys don't schedule extra work or get lost across merges.
    const pendingQueryKeys = useRef({})

    useEffect(() => {
        return () => {
            Object.values(debounceTimers.current).forEach(clearTimeout)
            debounceTimers.current = {}
            pendingQueryKeys.current = {}
        }
    }, [])

    const invalidateForTicket = useCallback((ticketId, queryKeys) => {
        if (!pendingQueryKeys.current[ticketId]) {
            pendingQueryKeys.current[ticketId] = new Map()
        }
        const keysForTicket = pendingQueryKeys.current[ticketId]
        queryKeys.forEach((key) => {
            keysForTicket.set(JSON.stringify(key), key)
        })

        if (debounceTimers.current[ticketId]) {
            clearTimeout(debounceTimers.current[ticketId])
        }
        debounceTimers.current[ticketId] = setTimeout(() => {
            keysForTicket.forEach((key) => {
                queryClient.invalidateQueries(key, { refetchActive: true })
            })
            delete debounceTimers.current[ticketId]
            delete pendingQueryKeys.current[ticketId]
        }, DEBOUNCE_MS)
    }, [queryClient])

    useEffect(() => {
        let isStale = false

        const resolveSignalRNotification = async (payload) => {
            /*****************************************
            This is where we split apart the message, 
            resolve the entity and id and check that 
            it is one for the user and return back the
            object used to populate the notification with.
            *****************************************/
        
            if (!payload) {
                return
            }
            // We could resolve the contact performing the action from the payload.PersonId here to be able to augment the message...
            // const currentCompany = getLocalStorage(process.env.REACT_APP_COMPANY_KEY)
            // const contacts = await fetchContacts()
            // const contact = contacts.Items.find(c => c.Id === payload.PersonId)
        
            // Create a new object for return and set the default return as not valid for this user in the company they are viewing and the sites that they have permissions to see
            const entityTypeText = payload.entityType === 'TicketNote'
                ? 'Ticket Note'
                : payload.entityType === 'TicketAttachment'
                    ? 'Ticket Attachment'
                    : payload.entityType

            let rtn = {
                isValid: false,
                title: `${entityTypeText} ${payload.action}d`,
                // by: contact ? `${contact.firstName} ${contact.lastName}` : undefined,
            }

            // Check each entity to suss out how to verify it is for us and set up the return object
            if (payload.entityType === 'Ticket') {
                const ticket = await fetchTicketById(payload.entityId)
                if (ticket.Items?.length > 0) {
                    const ticketNumber = ticket.Items[0].ticketNumber
                    const messagesByAction = {
                        Create: `Ticket number: ${ticketNumber} has been created. `,
                        Delete: `Ticket number: ${ticketNumber} has been removed. `,
                        Update: `Ticket number: ${ticketNumber} has been updated. `,
                    }
                    rtn = {
                        ...rtn,
                        isValid: true,
                        ticketId: ticket.Items[0].id,
                        lastActivityDate: ticket.Items[0].lastActivityDate || new Date().toISOString(),
                        message: messagesByAction[payload.action],
                    }
                }
            } else if (payload.entityType === 'TicketNote') {
                const ticketNote = await fetchTicketNoteById(payload.entityId)
                if (ticketNote.Items?.length > 0) {
                    const ticket = await fetchTicketById(ticketNote.Items[0].ticketID)
                    if (ticket.Items?.length > 0) {
                        const ticketNumber = ticket.Items[0].ticketNumber
                        const messagesByAction = {
                            Create: `Ticket number: ${ticketNumber} has a new note. `,
                            Delete: `Ticket number: ${ticketNumber} has a note removed. `,
                            Update: `Ticket number: ${ticketNumber} has a note updated. `,
                        }
                        rtn = {
                            ...rtn,
                            isValid: true,
                            ticketId: ticket.Items[0].id,
                            noteId: ticketNote.Items[0].id,
                            lastActivityDate: ticketNote.Items[0].lastActivityDate || ticketNote.Items[0].createDateTime || new Date().toISOString(),
                            message: messagesByAction[payload.action],
                        }
                    }
                }
            } else if (payload.entityType === 'TicketAttachment') {
                const ticketAttachment = await fetchTicketAttachmentById(payload.entityId)
                if (ticketAttachment.Items?.length > 0) {
                    const ticket = await fetchTicketById(ticketAttachment.Items[0].ticketID)
                    if (ticket.Items?.length > 0) {
                        const ticketNumber = ticket.Items[0].ticketNumber
                        const messagesByAction = {
                            Create: `Ticket number: ${ticketNumber} has a new attachment. `,
                            Delete: `Ticket number: ${ticketNumber} has an attachment removed. `,
                        }
                        rtn = {
                            ...rtn,
                            isValid: true,
                            ticketId: ticket.Items[0].id,
                            attachmentId: ticketAttachment.Items[0].id,
                            lastActivityDate: ticketAttachment.Items[0].createdDate || new Date().toISOString(),
                            message: messagesByAction[payload.action],
                        }
                    }
                }
            }
            if (rtn.isValid && !isStale) {
                // Live Update Store writes + query invalidation
                const store = useLiveUpdateStore.getState()

                if (payload.entityType === 'Ticket') {
                    if (payload.action === 'Create') {
                        store.markTicketNew(rtn.ticketId, rtn.lastActivityDate)
                    } else {
                        store.markTicketUpdated(rtn.ticketId, rtn.lastActivityDate)
                    }
                    invalidateForTicket(rtn.ticketId, [
                        ['tickets'],
                        ['alertTickets'],
                    ])
                } else if (payload.entityType === 'TicketNote') {
                    if (payload.action === 'Create') {
                        store.markNoteNew(rtn.ticketId, rtn.noteId, rtn.lastActivityDate)
                    } else if (payload.action === 'Update') {
                        store.markNoteUpdated(rtn.ticketId, rtn.noteId, rtn.lastActivityDate)
                    }
                    invalidateForTicket(rtn.ticketId, [
                        ['tickets'],
                        ['alertTickets'],
                        ['ticket-comments', rtn.ticketId],
                        ['ticketNoteCount', rtn.ticketId],
                    ])
                } else if (payload.entityType === 'TicketAttachment') {
                    if (payload.action === 'Create') {
                        store.markAttachmentNew(rtn.ticketId, rtn.attachmentId, rtn.lastActivityDate)
                    }
                    invalidateForTicket(rtn.ticketId, [
                        ['tickets'],
                        ['alertTickets'],
                        ['ticket-attachments', rtn.ticketId],
                        ['ticketAttachmentCount', rtn.ticketId],
                    ])
                }

                // Toast / Bell notification (existing behaviour)
                //console.log("addNotification", rtn.title, rtn.message, rtn.ticketId, rtn.by)
                addNotification({title: rtn.title, message: rtn.message, ticketId: rtn.ticketId, person: rtn.by})
            }
        }

        resolveSignalRNotification(message).catch((error) => {
            console.error('SignalRDataSync: failed to process SignalR message', error)
        })

        return () => {
            isStale = true
        }
    }, [message, addNotification, invalidateForTicket])

    return null
}

export default SignalRDataSync